import {useMemo} from 'react'
import {useProductSearch} from '@salesforce/commerce-sdk-react'

import {useSitePreferences} from '../../../../../hooks/use-site-preferences'
import {useBrand} from '../../../../../contexts/brand-context'
import {isEarlyAccessSource} from './helpers'

const EARLY_ACCESS_PREFERENCE_ID = 'sflmEarlyAccessConfig'
const DEFAULT_PRODUCTS_COUNT = 12

/* The `sflmEarlyAccessConfig` site preference is stored as a JSON string
 * (custom-object style). Accept an already-parsed object too so the hook is
 * resilient to whichever shape the preferences API returns. */
const parseEarlyAccessConfig = (value) => {
    if (!value) return null
    if (typeof value === 'object') return value
    if (typeof value !== 'string') return null
    try {
        const parsed = JSON.parse(value)
        return parsed && typeof parsed === 'object' ? parsed : null
    } catch {
        return null
    }
}

/**
 * Converts the "Early Access" carousel dynamic source into renderable product
 * tiles. Unlike the VickyAI / Einstein sources, Early Access is driven entirely
 * by SFCC: it reads the `sflmEarlyAccessConfig` site preference, resolves the
 * brand-specific category (`cgid`), then runs a standard product search with the
 * authored sorting rule and product count. The resulting search hits are mapped
 * to `GDIProductTileBlock` pseudo-tiles consumable by `renderComponentsSwitch`.
 *
 * @param {?string} dynamicContent - CMS dynamic source label for the carousel.
 * @returns {{tiles: Array<object>, isLoading: boolean}} Tiles + loading state.
 */
export const useEarlyAccessTiles = (dynamicContent) => {
    const shouldFetch = isEarlyAccessSource(dynamicContent)
    const {isGarage} = useBrand()
    const {data: preferences} = useSitePreferences(
        shouldFetch ? EARLY_ACCESS_PREFERENCE_ID : []
    )

    const config = useMemo(
        () => parseEarlyAccessConfig(preferences?.[EARLY_ACCESS_PREFERENCE_ID]),
        [preferences]
    )
    const brandKey = isGarage ? 'grg' : 'dyn'
    const categoryId = config?.cgid?.[brandKey]
    const productsCount = Number(config?.productsCount) || DEFAULT_PRODUCTS_COUNT
    const sortingRule = config?.sortingRule
    const canFetch = shouldFetch && config?.enabled === true && Boolean(categoryId)

    const {data, isLoading} = useProductSearch(
        {
            parameters: {
                refine: [`cgid=${categoryId}`],
                limit: productsCount,
                ...(sortingRule ? {sort: sortingRule} : {}),
                perPricebook: true,
                allImages: true,
                allVariationProperties: true,
                expand: [
                    'prices',
                    'images',
                    'variations',
                    'custom_properties',
                    'promotions'
                ]
            }
        },
        {
            enabled: canFetch,
            staleTime: 15 * 60 * 1000
        }
    )

    const tiles = useMemo(() => {
        const hits = data?.hits || []
        return hits.map((hit) => ({
            __typename: 'GDIProductTileBlock',
            Product: hit,
            ContentLink: {GuidValue: `early-access-${hit.productId}`}
        }))
    }, [data])

    if (!shouldFetch) return {tiles: [], isLoading: false}

    return {tiles, isLoading: canFetch && isLoading}
}
